<template>
  <div>
    <div
      class="d-flex justify-content-between align-items-start flex-wrap mb-3"
    >
      <!-- CARD HEADER -->
      <h4 class="mb-0">
        {{ sectionHeader }}
        <span class="ms-2">
          <HelpCenterButton
            :content-key="sectionContentKey"
            aria-label="Emergency contacts list help"
          />
        </span>
      </h4>
      <!-- CARD HEADER -->

      <!-- CARD CALL TO ACTION -->
      <BaseButton
        solid
        rounded
        prepend-icon="ri-add-line"
        @click="onAddEmergencyContact()"
      >
        {{ buttonText }}
      </BaseButton>
      <!-- CARD CALL TO ACTION -->
    </div>

    <!-- SERVER ERROR -->
    <VisitorErrorHandler
      v-if="serverError != null"
      :error="serverError"
      @done="serverError = null"
    />
    <!-- SERVER ERROR -->

    <!-- PAGE SELECTOR -->
    <Perpage
      v-if="!isLoading && !isSchoolSettingsLoading"
      use-first-option-as-default
      use-dynamic-update
      showing-entries
      :options="pageOptions"
      :pagination="paginationWithExtra"
      @on-page-change="setPage"
    />
    <!-- PAGE SELECTOR -->

    <!-- TABLE -->
    <DataTable
      :class="{ 'visitor-loading': isLoading || isSchoolSettingsLoading }"
      :fields="fields"
      :items="list"
    >
      <template #first_name="{ item }">
        <td>
          {{ item.first_name }}
        </td>
      </template>
      <template #last_name="{ item }">
        <td>
          {{ item.last_name }}
        </td>
      </template>
      <template #email_address="{ item }">
        <td>
          {{ item.email_address?.join(", ") }}
        </td>
      </template>
      <template #phone_number="{ item }">
        <td>
          {{ item.phone_number?.join(", ") }}
        </td>
      </template>
      <template #action="{ item }">
        <td class="text-end">
          <Actions
            v-if="!isLoading && !isSchoolSettingsLoading"
            :actions="actions"
            @click-action="handleTableAction($event, item.id)"
          >
          </Actions>
        </td>
      </template>
    </DataTable>
    <!-- TABLE -->

    <!-- PAGINATION -->
    <Pagination
      v-if="pagination.pages > 1 && !isLoading && !isSchoolSettingsLoading"
      :active-page="pagination.activePage"
      :pages="pagination.pages"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      size="lg"
      align="center"
      @update:active-page="setActivePage"
    ></Pagination>
    <!-- PAGINATION -->
  </div>
</template>

<script>
import VisitorErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import HelpCenterButton from "@/v3components/HelpCenterButton.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import Actions from "@/v3components/shared/DataTable/Actions.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import Perpage from "@/v3components/shared/DataTable/Perpage.vue"
import visitorConstants from "@/constants/visitorConstants"
import { computed, defineAsyncComponent, inject, ref } from "vue"
import { useStore } from "vuex"
import { onMounted } from "vue"

const VisitorCreateEmergencyContactForm = defineAsyncComponent(
  () =>
    import("@/v3components/Forms/Visitor/ListSettings/VisitorCreateEmergencyContactForm.vue")
)

export default {
  name: "VisitorEmergencyContactTable",
  components: {
    DataTable,
    Actions,
    Perpage,
    BaseButton,
    VisitorErrorHandler,
    HelpCenterButton
  },
  props: {
    isSchoolSettingsLoading: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const actionDialog = inject("actionDialog")
    const modal = inject("modal")
    const store = useStore()
    const serverError = ref(null)

    const pageOptions = [
      { label: "25 / Page", value: 25 },
      { label: "50 / Page", value: 50 },
      { label: "100 / Page", value: 100 }
    ]

    const fields = [
      { key: "first_name", label: "First name", sorter: false, filter: false },
      { key: "last_name", label: "Last name", sorter: false, filter: false },
      {
        key: "email_address",
        label: "Email Address",
        sorter: false,
        filter: false
      },
      {
        key: "phone_number",
        label: "Phone Number",
        sorter: false,
        filter: false
      },
      {
        key: "action",
        label: "Action",
        _style: "width: 50px;",
        sorter: false,
        filter: false
      }
    ]

    const actions = [
      {
        label: "Delete",
        icon: "ri-delete-bin-line",
        class: "important",
        action: "delete"
      }
    ]

    const emergencyContact = computed(
      () => store.getters["visitorSettings/emergencyContact"]
    )

    const list = computed(() => emergencyContact?.value?.data || [])

    const pagination = computed(() => emergencyContact?.value?.pagination || {})

    const paginationWithExtra = computed(() => {
      return {
        ...pagination.value,
        from:
          Number(pagination?.value?.activePage - 1) *
            Number(pagination?.value?.per_page?.value) +
          1,
        to:
          Number(pagination?.value?.activePage - 1) *
            Number(pagination?.value?.per_page?.value) +
          Number(pagination?.value?.per_page?.value)
      }
    })

    const isLoading = computed(
      () => emergencyContact?.value?.isLoading || false
    )

    const shouldGoBackOnePage = computed(() => {
      const isActivePageGreaterThanOne =
        Number(pagination?.value?.activePage) > 1
      const isTotalPagesGreaterThanOne = Number(pagination?.value?.pages) > 1
      const isLastPage =
        Number(pagination?.value?.activePage) ===
        Number(pagination?.value?.pages)
      const onlyOneItemLeft = Number(list?.value?.length) === 1
      return (
        isActivePageGreaterThanOne &&
        isTotalPagesGreaterThanOne &&
        isLastPage &&
        onlyOneItemLeft
      )
    })

    const setPage = async (option) => {
      if (!option) return

      store.commit(
        "visitorSettings/SET_EMERGENCYCONTACT_PAGINATION_PER_PAGE",
        option
      )
      await store.dispatch("visitorSettings/getEmergencyContacts")
    }

    const setActivePage = async (page) => {
      if (!page) return

      store.commit(
        "visitorSettings/SET_EMERGENCYCONTACT_PAGINATION_ACTIVE_PAGE",
        page
      )
      await store.dispatch("visitorSettings/getEmergencyContacts")
    }

    const handleTableAction = (event, item) => {
      if (event.action == "delete") {
        actionDialog.open(deleteItem, {
          args: item,
          props: { danger: true, title: "Delete", question: "Are you sure?" }
        })
      }
    }

    const deleteItem = async (adultId) => {
      try {
        await store.dispatch("visitorSettings/deleteEmergencyContact", adultId)
        if (shouldGoBackOnePage?.value) {
          setActivePage(Number(pagination?.value?.activePage) - 1)
        }
        await store.dispatch("visitorSettings/getEmergencyContacts")
      } catch (error) {
        serverError.value = error
      }
    }

    const onAddEmergencyContact = () => {
      modal.open(VisitorCreateEmergencyContactForm, {
        size: "sm",
        title: visitorConstants.LIST_SETTINGS.EMERGENCY_CONTACT_MODAL_TITLE,
        props: {}
      })
    }

    onMounted(async () => {
      try {
        await store.dispatch("visitorSettings/getEmergencyContacts")
      } catch (error) {
        serverError.value = error
      }
    })

    return {
      isLoading,
      serverError,
      buttonText: visitorConstants.LIST_SETTINGS.EMERGENCY_CONTACT_ADD_BUTTON,
      sectionHeader:
        visitorConstants.LIST_SETTINGS.EMERGENCY_CONTACT_SECTION_HEADER,
      sectionContentKey:
        visitorConstants.LIST_SETTINGS.EMERGENCY_CONTACT_LEARN_MORE,
      list,
      pageOptions,
      pagination,
      paginationWithExtra,
      fields,
      actions,
      handleTableAction,
      setPage,
      setActivePage,
      onAddEmergencyContact
    }
  }
}
</script>
